import React from 'react';
import { WhiteboardProps } from '@dreamai/types';
import './whiteboard-component.scss';

interface WordItem {
  word: string;
  translation?: string;
}

interface SentenceItem {
  sentence: string;
  translation?: string;
}

export interface WhiteboardComponentProps extends WhiteboardProps {
  threadId?: string;
}

const WhiteboardComponent: React.FC<WhiteboardComponentProps> = ({
  contentType,
  text,
  translation,
  words: rawWords,
  sentences: rawSentences,
  tableHeadings: rawHeadings,
  tableRows: rawRows,
  htmlContent,
  fontSize = 'large',
}) => {
  // Parse data - may come as JSON strings from database
  const parseIfNeeded = (data: any): any => {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return data;
      }
    }
    return data;
  };

  const tableHeadings = parseIfNeeded(rawHeadings);
  const tableRows = parseIfNeeded(rawRows);
  const words: WordItem[] = parseIfNeeded(rawWords) || [];
  const sentences: SentenceItem[] = parseIfNeeded(rawSentences) || [];

  const renderContent = () => {
    switch (contentType) {
      case 'letter':
        return (
          <div className='letter-content'>
            <span className='letter-text'>{text}</span>
          </div>
        );

      case 'word':
        return (
          <div className='word-content'>
            <span className='word-text'>{text}</span>
            {translation && <span className='translation'>{translation}</span>}
          </div>
        );

      case 'sentence':
        return (
          <div className='sentence-content'>
            <p className='sentence-text'>{text}</p>
            {translation && <p className='translation'>{translation}</p>}
          </div>
        );

      case 'table':
        return (
          <div className='table-content'>
            <table className='whiteboard-table'>
              {tableHeadings && tableHeadings.length > 0 && (
                <thead>
                  <tr>
                    {tableHeadings.map((heading: string, idx: number) => (
                      <th key={idx}>{heading}</th>
                    ))}
                  </tr>
                </thead>
              )}
              {tableRows && tableRows.length > 0 && (
                <tbody>
                  {tableRows.map((row: any, rowIdx: number) => {
                    const parsedRow = parseIfNeeded(row);
                    if (!Array.isArray(parsedRow)) return null;
                    return (
                      <tr key={rowIdx}>
                        {parsedRow.map((cell: string, cellIdx: number) => (
                          <td key={cellIdx}>{cell}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
        );

      case 'freeform':
        return (
          <div
            className='freeform-content'
            dangerouslySetInnerHTML={{ __html: htmlContent || '' }}
          />
        );

      case 'words':
        return (
          <div className='words-grid'>
            {words.length === 0 ? (
              <div className='empty'>No words yet</div>
            ) : (
              words.map((item, idx) => (
                <div key={idx} className='word-card'>
                  <span className='word-text'>{item.word}</span>
                  {item.translation && (
                    <span className='translation'>{item.translation}</span>
                  )}
                </div>
              ))
            )}
          </div>
        );

      case 'sentences':
        return (
          <div className='sentences-grid'>
            {sentences.length === 0 ? (
              <div className='empty'>No sentences yet</div>
            ) : (
              sentences.map((item, idx) => (
                <div key={idx} className='sentence-card'>
                  <p className='sentence-text'>{item.sentence}</p>
                  {item.translation && (
                    <p className='translation'>{item.translation}</p>
                  )}
                </div>
              ))
            )}
          </div>
        );

      default:
        return <div className='empty'>No content</div>;
    }
  };

  return (
    <div className={`whiteboard whiteboard-size-${fontSize}`}>
      <div className='board'>{renderContent()}</div>
    </div>
  );
};

export default WhiteboardComponent;
